import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { SITE } from "@/config/site";
import { useLang } from "@/contexts/LanguageContext";

export default function Footer() {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();

  const links = [
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/projects", label: t.nav.projects },
    { to: "/clients", label: t.nav.clients },
    { to: "/manpower", label: t.nav.manpower },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 border-t border-white/5 bg-navy-900/80"
    >
      <div className="absolute inset-0 grid-blueprint opacity-30 pointer-events-none" />
      <div className="relative container-pad py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
              <img src={SITE.logoUrl} alt="EAH" className="w-12 h-12 rounded-full object-cover ring-1 ring-gold-500/30" />
              <div>
                <div className="font-heading font-extrabold text-white tracking-tight">
                  {lang === "ar" ? SITE.brand.ar : SITE.brand.en}
                </div>
                <div className="text-[11px] text-overline -mt-0.5">
                  {lang === "ar" ? "تصنيع الحديد والمقاولات" : SITE.brand.short + " · Engineering"}
                </div>
              </div>
            </Link>
            <p className="mt-5 text-sm text-white/65 leading-relaxed max-w-sm">{t.footer.tagline}</p>
            <div className="mt-5 metal-strip max-w-xs" />
            <div className="mt-5 text-xs text-white/50 font-mono">
              CR {SITE.contact.cr} · VAT {SITE.contact.vat}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-overline mb-4">{t.footer.quickLinks}</div>
            <ul className="space-y-2.5">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}
                    className="text-sm text-white/75 hover:text-gold-500 transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="text-overline mb-4">{t.footer.contact}</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-gold-500 shrink-0" />
                <span className="text-white/75">
                  {lang === "ar" ? SITE.contact.address.ar : SITE.contact.address.en}
                </span>
              </li>
              <li className="flex items-start gap-3 flex-wrap">
                <Phone className="w-4 h-4 mt-0.5 text-gold-500 shrink-0" />
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {SITE.contact.phones.map((p) => (
                    <a
                      key={p}
                      dir="ltr"
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="text-white/80 hover:text-gold-500"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3 flex-wrap">
                <Mail className="w-4 h-4 mt-0.5 text-gold-500 shrink-0" />
                <div className="flex flex-col">
                  {SITE.contact.emails.map((e) => (
                    <a key={e} href={`mailto:${e}`} className="text-white/80 hover:text-gold-500">
                      {e}
                    </a>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div data-testid="footer-copyright">{t.footer.copyright.replace("{year}", year)}</div>
          <div className="font-mono tracking-wider">{SITE.contact.website}</div>
        </div>
      </div>
    </footer>
  );
}
